import { useState } from "react";
import {
    PartyPopper,
    Phone,
    ArrowRight,
    CheckCircle2,
    Loader2,
    CalendarCheck,
    BadgePercent,
} from "lucide-react";
import { motion } from "framer-motion";
import { SUMMER_OFFER, COMPANY } from "../data/content";
import { submitLead } from "../lib/api";

export default function SummerOffer() {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        postcode: "",
        message: "",
    });
    const [status, setStatus] = useState({
        loading: false,
        success: false,
        error: "",
    });

    const update = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (status.loading) return;
        if (!form.name.trim() || form.name.trim().length < 2) {
            setStatus({
                loading: false,
                success: false,
                error: "Please add your name.",
            });
            return;
        }
        const digits = form.phone.replace(/\D/g, "");
        if (digits.length < 7) {
            setStatus({
                loading: false,
                success: false,
                error: "Please add a valid UK phone number.",
            });
            return;
        }
        setStatus({ loading: true, success: false, error: "" });
        try {
            await submitLead({
                name: form.name.trim(),
                phone: form.phone.trim(),
                postcode: form.postcode.trim() || null,
                message: form.message.trim() || null,
                source: "summer-offer-2026",
                offer: "SUMMER 2026 — 10% off all landscaping projects",
            });
            setStatus({ loading: false, success: true, error: "" });
            setForm({ name: "", phone: "", postcode: "", message: "" });
        } catch (err) {
            setStatus({
                loading: false,
                success: false,
                error:
                    err.message ||
                    `Something went wrong. Please call us on ${COMPANY.phone}.`,
            });
        }
    };

    return (
        <section
            id="summer-offer"
            data-testid="summer-offer-section"
            className="relative bg-haas-sand py-20 sm:py-28 overflow-hidden"
        >
            {/* decorative blobs */}
            <div className="pointer-events-none absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-haas-leaf/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -right-24 w-[480px] h-[480px] rounded-full bg-haas-gold/25 blur-3xl" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
                    {/* LEFT — offer poster */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-7"
                        data-testid="summer-offer-poster"
                    >
                        <div className="rounded-3xl bg-white border-2 border-haas-ink shadow-[10px_10px_0_#123D28] p-8 sm:p-12">
                            {/* Summer badge */}
                            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-haas-gold border-2 border-haas-ink shadow-[3px_3px_0_#123D28]">
                                <PartyPopper className="w-4 h-4 text-haas-ink" />
                                <span className="text-haas-ink font-bold text-xs uppercase tracking-[0.25em]">
                                    {SUMMER_OFFER.badge}
                                </span>
                            </div>

                            {/* Headline */}
                            <h2
                                data-testid="summer-offer-headline"
                                className="mt-7 font-display font-bold text-6xl sm:text-8xl lg:text-9xl text-haas-ink leading-[0.95] tracking-tight"
                            >
                                {SUMMER_OFFER.headline}
                            </h2>
                            <p className="mt-5 text-haas-soft text-base sm:text-lg leading-relaxed max-w-xl">
                                on{" "}
                                <span className="font-bold text-haas-goldDeep">
                                    all landscaping projects
                                </span>{" "}
                                booked this{" "}
                                <span className="font-bold text-haas-ink">
                                    Summer 2026
                                </span>{" "}
                                — artificial grass, patios, driveways &amp;
                                fencing.
                            </p>

                            {/* Perks split */}
                            <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="rounded-2xl bg-haas-leaf/15 border-2 border-haas-ink shadow-[5px_5px_0_#123D28] p-5 sm:p-6 flex items-center gap-4">
                                    <div className="w-11 h-11 rounded-xl bg-haas-leaf border-2 border-haas-ink flex items-center justify-center flex-shrink-0">
                                        <CalendarCheck className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="font-display font-bold text-base sm:text-lg text-haas-ink leading-tight">
                                        Free site visit
                                    </div>
                                </div>
                                <div className="rounded-2xl bg-haas-ink border-2 border-haas-ink shadow-[5px_5px_0_#D4AF37] p-5 sm:p-6 flex items-center gap-4">
                                    <div className="w-11 h-11 rounded-xl bg-haas-gold border-2 border-haas-ink flex items-center justify-center flex-shrink-0">
                                        <BadgePercent className="w-5 h-5 text-haas-ink" />
                                    </div>
                                    <div className="font-display font-bold text-base sm:text-lg text-white leading-tight">
                                        No-obligation quote
                                    </div>
                                </div>
                            </div>
                            <p className="mt-5 text-haas-soft text-sm leading-relaxed">
                                {SUMMER_OFFER.note}
                            </p>

                            {/* quick-call CTA */}
                            <a
                                href={`tel:${COMPANY.phoneTel}`}
                                data-testid="summer-offer-quick-call"
                                className="mt-7 inline-flex items-center gap-3 px-6 py-3.5 rounded-full bg-haas-ink text-haas-cream hover:bg-haas-goldDeep hover:text-haas-ink font-bold text-sm transition-colors shadow-[4px_4px_0_#D4AF37]"
                            >
                                <Phone className="w-4 h-4" />
                                Or call now · {COMPANY.phone}
                            </a>
                        </div>
                    </motion.div>

                    {/* RIGHT — form card */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="lg:col-span-5"
                        data-testid="summer-offer-form-wrap"
                    >
                        <form
                            onSubmit={onSubmit}
                            data-testid="summer-offer-form"
                            className="rounded-3xl bg-haas-ink text-haas-cream border-2 border-haas-ink shadow-[10px_10px_0_#D4AF37] p-8 sm:p-10"
                        >
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-haas-gold border-2 border-haas-cream/30">
                                <span className="text-haas-ink text-[10px] uppercase tracking-[0.22em] font-bold">
                                    Limited spots
                                </span>
                            </div>
                            <h3
                                data-testid="summer-offer-form-headline"
                                className="mt-5 font-display font-bold text-3xl sm:text-4xl leading-tight"
                            >
                                {SUMMER_OFFER.formHeadline}
                            </h3>
                            <p className="mt-3 text-haas-cream/70 text-sm leading-relaxed">
                                {SUMMER_OFFER.formNote}
                            </p>

                            {status.success ? (
                                <div
                                    data-testid="summer-offer-success"
                                    className="mt-7 rounded-2xl bg-haas-gold/15 border-2 border-haas-gold p-6 text-haas-cream"
                                >
                                    <div className="flex items-center gap-3 mb-2">
                                        <CheckCircle2 className="w-6 h-6 text-haas-gold" />
                                        <span className="font-display font-bold text-xl">
                                            Got it — speak soon!
                                        </span>
                                    </div>
                                    <p className="text-sm text-haas-cream/80">
                                        Thanks for your interest. We'll call you
                                        back within one working day to lock in
                                        your 10% off.
                                    </p>
                                </div>
                            ) : (
                                <div className="mt-7 space-y-3">
                                    <Field
                                        id="name"
                                        label="Full name"
                                        value={form.name}
                                        onChange={update("name")}
                                        placeholder="e.g. James Whitcombe"
                                        autoComplete="name"
                                        required
                                    />
                                    <Field
                                        id="phone"
                                        label="Phone number"
                                        value={form.phone}
                                        onChange={update("phone")}
                                        placeholder="07412 324397"
                                        autoComplete="tel"
                                        inputMode="tel"
                                        type="tel"
                                        required
                                    />
                                    <Field
                                        id="postcode"
                                        label="Postcode (optional)"
                                        value={form.postcode}
                                        onChange={update("postcode")}
                                        placeholder="CV1 1AA"
                                        autoComplete="postal-code"
                                    />
                                    <div>
                                        <label
                                            htmlFor="message"
                                            className="block text-[11px] uppercase tracking-[0.22em] font-bold text-haas-gold mb-1.5"
                                        >
                                            What are you planning? (optional)
                                        </label>
                                        <textarea
                                            id="message"
                                            data-testid="summer-offer-input-message"
                                            value={form.message}
                                            onChange={update("message")}
                                            rows={3}
                                            placeholder="e.g. Artificial grass + new porcelain patio"
                                            className="w-full rounded-2xl bg-white/5 border-2 border-haas-cream/15 focus:border-haas-gold focus:outline-none text-haas-cream placeholder:text-haas-cream/40 px-4 py-3 text-sm resize-none transition-colors"
                                        />
                                    </div>

                                    {status.error && (
                                        <div
                                            data-testid="summer-offer-error"
                                            className="rounded-xl bg-red-500/15 border border-red-400/40 text-red-200 px-4 py-3 text-sm"
                                        >
                                            {status.error}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        data-testid="summer-offer-submit"
                                        disabled={status.loading}
                                        className="mt-2 w-full inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full bg-haas-gold text-haas-ink hover:bg-haas-goldDeep border-2 border-haas-cream/20 font-bold text-sm transition-colors disabled:opacity-70 disabled:cursor-not-allowed shadow-[4px_4px_0_#D4AF37]"
                                    >
                                        {status.loading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Sending…
                                            </>
                                        ) : (
                                            <>
                                                Claim my 10% off
                                                <ArrowRight className="w-4 h-4" />
                                            </>
                                        )}
                                    </button>
                                    <p className="text-[11px] text-haas-cream/55 leading-relaxed">
                                        By submitting you agree we may contact
                                        you about your enquiry. No spam, no
                                        unwanted calls.
                                    </p>
                                </div>
                            )}
                        </form>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function Field({ id, label, ...rest }) {
    return (
        <div>
            <label
                htmlFor={id}
                className="block text-[11px] uppercase tracking-[0.22em] font-bold text-haas-gold mb-1.5"
            >
                {label}
            </label>
            <input
                id={id}
                data-testid={`summer-offer-input-${id}`}
                {...rest}
                className="w-full rounded-full bg-white/5 border-2 border-haas-cream/15 focus:border-haas-gold focus:outline-none text-haas-cream placeholder:text-haas-cream/40 px-5 py-3 text-sm transition-colors"
            />
        </div>
    );
}
